import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { getIsPWAInstalled } from 'helpers/getIsPWAInstalled';
import { getFlyingBetslipConfig } from 'helpers/prematch';
import { MenuLink } from 'interfaces';
import { isCasinoGamePage } from 'utils';

import { printMissingConfigError, SiteConfigManager, useSettings } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';

import NavigationBarItemList from './components/NavigationBarItemList';
import NavigationNotchBackground from './components/NavigationNotchBackground';
import { useFloatingNotch } from './hooks/useFloatingNotch';
import { BUTTONS_VARIANTS } from './constants';
import { isSupportedLinkPlatform } from './utils';

const NewNavigationBar = () => {
  // state of the betslip popup
  const { betIdToEdit } = useLocalBetslip();
  const { quickBet: isQuickBet } = useSettings();
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);
  const {
    location: { pathname },
  } = useHistory();
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);

  const betslipNavConfig = useMemo(() => getFlyingBetslipConfig(), []);
  const mobileBottomNavLinks = SiteConfigManager.getConfig<MenuLink[]>('navigation.mobile', []);

  const linksCount = betslipNavConfig.hasSimple || betslipNavConfig.hasExtended ? 6 : 5;

  const links = useMemo(
    () =>
      mobileBottomNavLinks
        .filter(el => isSupportedLinkPlatform(el))
        .slice(0, linksCount)
        .sort((a, b) => +a.order_index - +b.order_index)
        .map(
          ({
            alt_icon: altIcon,
            button_style: buttonStyle,
            icon,
            icon_type: iconType,
            light_icon: lightIcon,
            location,
            name,
            target,
          }) => ({
            altIcon,
            buttonStyle,
            icon,
            iconType,
            lightIcon,
            name,
            path: location,
            target: target === '_blank' ? '_blank' : '',
          }),
        ),
    [mobileBottomNavLinks, linksCount],
  );

  const hasHighlightedButton = useMemo(
    () => links.some(link => link.buttonStyle === BUTTONS_VARIANTS.highlighted),
    [links],
  );

  const floatingLink = useMemo(
    () => links.find(link => link.buttonStyle === BUTTONS_VARIANTS.floating),
    [links],
  );
  const hasFloatingButton = !!floatingLink;

  const { notch, tabBarRef } = useFloatingNotch(hasFloatingButton, floatingLink?.path);

  const navigationClass = cx('navigation new-nav', {
    hidden: isCasinoGamePage(pathname, [casinoPath, casinoLivePath]),
    'navigation--has-floating': hasFloatingButton,
    'navigation--has-highlighted': hasHighlightedButton,
    shadow: isQuickBet || betIdToEdit,
  });

  if (mobileBottomNavLinks.length === 0) {
    printMissingConfigError('navigation.mobile');
  }

  const navBarClass = cx('navigation-tab-bar', { 'is-pwa': getIsPWAInstalled() });

  return (
    <div className={navigationClass}>
      <div className={navBarClass} ref={tabBarRef}>
        {notch && <NavigationNotchBackground {...notch} />}
        <NavigationBarItemList isExtendedBetslipNavBar links={links} />
      </div>
    </div>
  );
};

export default NewNavigationBar;
